"use client"

import { useState, useEffect } from "react"
import { Box, Tabs, Tab, Typography, CircularProgress } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import DocumentCategorySection from "./DocumentCategorySection"
import { commonRunQuery } from "../../../util/API/loans"
import { getKYCDocs } from "../../../util/API/KYC_new"
import { getOptionsForSelect } from "../../../util/utilfunctions/utilFunctions"
import { stateData } from "../../../constants/countrylist"

const KYCUploadComponentFixed = ({ userType = "both", selectedUser }) => {
  console.log("KYCUploadComponentFixed-userType--------------", userType);
  const [activeTab, setActiveTab] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [expandedAccordion, setExpandedAccordion] = useState(null)
  const [userDocuments, setUserDocuments] = useState([])
  const [merchantDocuments, setMerchantDocuments] = useState([])
  const [documentImagesMap, setDocumentImagesMap] = useState({})
  const [documentCategories, setDocumentCategories] = useState({
    user: [],
    merchant: [],
  })
  const [subtypeLookup, setSubtypeLookup] = useState({})

  // Helper function to extract document info from avatar
  const extractDocInfoFromAvatar = (avatar) => {
    if (!avatar || avatar === "../../../images/profile-placeholder.jpg" || avatar === "../../../images/profile-placeholder.jpg") {
      return null
    }

    // Extract from format like "6459-XHJH7E8KDG-1-3648"
    const parts = avatar.split("-")
    if (parts.length >= 4) {
      const id = parts[parts.length - 1] // Last part: "3648"
      const name = parts.slice(0, -2).join("-") // Everything before last 2 parts: "6459-XHJH7E8KDG"

      return {
        id: id,
        name: name,
      }
    }

    return null
  }

  const extractFieldsFromString = (dataString) => {
    const headers = [
      "userid",
      "datatype",
      "docno",
      "docname",
      "docid",
      "birthdate",
      "expirydate",
      "country",
      "mdtype",
      "issuedate",
      "stateid",
    ]
    const values = dataString.split("|")

    const extractedObject = headers.reduce((obj, header, index) => {
      obj[header] = values[index] || null
      return obj
    }, {})

    return extractedObject
  }

  // Fixed callAccordionAPI function
  const callAccordionAPI = async (categoryId, isExpanded) => {
    if (!isExpanded) {
      return // Don't call API when closing accordion
    }

    try {
      // Get all documents for this category
      const categoryDocuments = getDocumentsByCategory(categoryId, activeTab == 0 ? userDocuments : merchantDocuments);

      // Extract document IDs from avatar field
      const docInfos = categoryDocuments
        .map((doc) => extractDocInfoFromAvatar(doc.avatar))
        .filter((info) => info !== null)

      // Prepare API data
      const apiData = {
        userid: selectedUser,
        docid: docInfos,
      }

      console.log("Calling accordion API with data:", apiData)
      console.log("Category ID:", categoryId)
      console.log("Documents in category:", categoryDocuments)
      console.log("Extracted doc IDs:", docInfos)

      // Call your API here
      const response = await getKYCDocs(selectedUser, docInfos)

      if (response?.callStatus === "1") {
        console.log("Accordion API response:=========", response)
        console.log("docsImageData:=========", response?.docs)
        console.log("docsSensitiveData:=========", response?.sensitiveData)

        const imageMap = {}
        // const updatedUserDocuments = [...userDocuments] // Create a copy
        const updatedUserDocuments = [
          ...(activeTab == 0 ? userDocuments : merchantDocuments)
        ];

        // Create a mapping of dataid to document for easier lookup
        const docIdToDocumentMap = {}
        categoryDocuments.forEach((doc) => {
          const docInfo = extractDocInfoFromAvatar(doc.avatar)
          if (docInfo) {
            docIdToDocumentMap[docInfo.id] = doc
          }
        })

        console.log("Document ID to Document Map:", docIdToDocumentMap)

        // Process each sensitive data item
        if (response?.docs && response?.sensitiveData) {
          for (let index = 0; index < response.sensitiveData.length; index++) {
            const sensitiveItem = response.sensitiveData[index]

            try {
              // Extract the fields from sensitiveData
              const extractedData = await extractFieldsFromString(sensitiveItem?.sensitiveData)
              console.log("Extracted data for dataid", sensitiveItem.dataid, ":", extractedData)

              // Find the correct document using dataid
              const targetDocument = docIdToDocumentMap[sensitiveItem.dataid]

              if (targetDocument) {
                // Find the index of this document in the updatedUserDocuments array
                const docIndex = updatedUserDocuments.findIndex((doc) => doc.id === targetDocument.id)
                console.log(`docIndex--------------`,docIndex)

                if (docIndex !== -1) {
                  // Update the specific document
                  updatedUserDocuments[docIndex] = {
                    ...updatedUserDocuments[docIndex],
                    number: extractedData?.docid || updatedUserDocuments[docIndex].number,
                    expiryDate: extractedData?.expirydate
                      ? new Date(extractedData.expirydate)
                      : updatedUserDocuments[docIndex].expiryDate,
                    issueDate: extractedData?.issuedate
                      ? new Date(extractedData.issuedate)
                      : updatedUserDocuments[docIndex].issueDate,
                    country: extractedData?.country || updatedUserDocuments[docIndex].country,
                    state: extractedData?.stateid || updatedUserDocuments[docIndex].state,
                  }

                  console.log(`Updated document with ID ${targetDocument.id}:`, updatedUserDocuments[docIndex])

                  // Map the image data
                  if (response.docs[index]) {
                    imageMap[sensitiveItem.dataid] = response.docs[index]
                  }
                } else {
                  console.warn(`Document with ID ${targetDocument.id} not found in updatedUserDocuments`)
                }
              } else {
                console.warn(`No document found for dataid: ${sensitiveItem.dataid}`)
              }
            } catch (error) {
              console.error(`Error processing sensitive data at index ${index}:`, error)
            }
          }

          // Update the state with all changes
          if(activeTab == 0) {
            setUserDocuments(updatedUserDocuments)
          } else {
            setMerchantDocuments(updatedUserDocuments)
          }
          console.log("All documents updated:", updatedUserDocuments)
        }

        // Also map by the extracted document IDs for fallback
        docInfos.forEach((docInfo, index) => {
          if (response?.docs && response.docs[index]) {
            imageMap[docInfo.id] = response.docs[index]
          }
        })

        console.log("Created image map:", imageMap)

        // Store the image mapping
        setDocumentImagesMap((prev) => ({
          ...prev,
          [categoryId]: imageMap,
        }))
      }
    } catch (error) {
      console.error("Error calling accordion API:", error)
    }
  }

  console.log("IMAGE MAP---------------",documentImagesMap);

  const refreshDocumentDetails = async (categoryId, justUpdatedDocId = null, newStatus = null, justUpdatedNote = null) => {
    // Get all documents for this category
    const categoryDocuments = getDocumentsByCategory(categoryId, activeTab == 0 ? userDocuments : merchantDocuments);
  
    // Extract document IDs from avatar field
    const docInfos = categoryDocuments
      .map((doc) => extractDocInfoFromAvatar(doc.avatar))
      .filter((info) => info !== null);
  
    // Call getKYCDocs
    const response = await getKYCDocs(selectedUser, docInfos);
    // console.log("refreshDocumentDetails-----------", response);
    // console.log("refreshDocumentDetails-----userDocuments------", userDocuments);
    // console.log("refreshDocumentDetails-----justUpdatedDocId------", justUpdatedDocId);
    // console.log("refreshDocumentDetails-----justUpdatedNote------", justUpdatedNote);
    // console.log("refreshDocumentDetails-----categoryId------", categoryId);
    // console.log("refreshDocumentDetails-----newStatus------", newStatus);
  
    if (response?.callStatus === "1" && response?.docs && response?.sensitiveData) {
      const imageMap = {};
      // const updatedUserDocuments = [...userDocuments];
      const updatedUserDocuments = [
        ...(activeTab == 0 ? userDocuments : merchantDocuments)
      ];
  
      // Map dataid to document
      const docIdToDocumentMap = {};
      categoryDocuments.forEach((doc) => {
        const docInfo = extractDocInfoFromAvatar(doc.avatar);
        if (docInfo) {
          docIdToDocumentMap[docInfo.id] = doc;
        }
      });
  
      for (let index = 0; index < response.sensitiveData.length; index++) {
        const sensitiveItem = response.sensitiveData[index];
        const extractedData = extractFieldsFromString(sensitiveItem?.sensitiveData);
        const targetDocument = docIdToDocumentMap[sensitiveItem.dataid];
  
        if (targetDocument) {
          const docIndex = updatedUserDocuments.findIndex((doc) => doc.id === targetDocument.id);
          if (docIndex !== -1) {
            updatedUserDocuments[docIndex] = {
              ...updatedUserDocuments[docIndex],
              // Preserve the status from the previous state!
              status: (justUpdatedDocId && updatedUserDocuments[docIndex].id === justUpdatedDocId)
              ? newStatus
              : updatedUserDocuments[docIndex].status,
              notes: (justUpdatedDocId && updatedUserDocuments[docIndex].id === justUpdatedDocId && justUpdatedNote !== undefined)
            ? justUpdatedNote
            : updatedUserDocuments[docIndex].notes,
              number: extractedData?.docid || updatedUserDocuments[docIndex].number,
              expiryDate: extractedData?.expirydate
                ? new Date(extractedData.expirydate)
                : updatedUserDocuments[docIndex].expiryDate,
              issueDate: extractedData?.issuedate
                ? new Date(extractedData.issuedate)
                : updatedUserDocuments[docIndex].issueDate,
              country: extractedData?.country || updatedUserDocuments[docIndex].country,
              state: extractedData?.stateid || updatedUserDocuments[docIndex].state,
            };
            if (response.docs[index]) {
              imageMap[sensitiveItem.dataid] = response.docs[index];
            }
          }
        }
      }
      console.log("refreshDocumentDetails--------updatedUserDocuments---", updatedUserDocuments);
  
      if(activeTab == 0) {
        setUserDocuments(updatedUserDocuments)
      } else {
        setMerchantDocuments(updatedUserDocuments)
      }

      setDocumentImagesMap((prev) => ({
        ...prev,
        [categoryId]: imageMap,
      }));
    }
  };

  const getDocumentsByCategory = (categoryId, documents) => {
    return documents.filter((doc) => doc.categoryId === categoryId)
  }

  const getUserKycData = async (uploadedImageData = null, categoryId = null) => {
    setIsLoading(true)
    try {
      const response = await commonRunQuery(activeTab == 0 ? 305 : 309, selectedUser)
      if (response?.data?.header?.callStatus === "1") {
        const userKycDetailsArr = Array.isArray(response?.data?.body?.record)
          ? response.data.body.record
          : [response.data.body.record]

        console.log("userKycDetailsArr=============", userKycDetailsArr)
        const transformed = transformKycDocs(userKycDetailsArr)
        if(activeTab == 0) {
          setUserDocuments(transformed)
        } else {
          setMerchantDocuments(transformed)
        }

        // If we have uploaded image data, find the newest document and map the image
        if (uploadedImageData && categoryId) {
          const categoryDocs = transformed.filter((doc) => doc.categoryId === categoryId)
          const newestDoc = categoryDocs.reduce((latest, current) => {
            return new Date(current.uploadDate) > new Date(latest.uploadDate) ? current : latest
          }, categoryDocs[0])

          if (newestDoc) {
            setDocumentImagesMap((prev) => {
              const cleanedMap = Object.keys(prev).reduce((acc, key) => {
                if (!key.startsWith("temp_")) {
                  acc[key] = prev[key]
                }
                return acc
              }, {})

              return {
                ...cleanedMap,
                [categoryId]: {
                  ...cleanedMap[categoryId],
                  [newestDoc.documentImageId || newestDoc.id]: uploadedImageData,
                },
              }
            })
          }
        }

        console.log("transformed=============", transformed)
      }
    } catch (error) {
      console.log("getUserKycData_error=============", error)
    } finally {
      setIsLoading(false)
    }
  }

  const statusMap = {
    "1": "pending",
    "2": "approved",
    "3": "rejected",
    "4": "expired",
    "5": "deleted"
  };

  const transformKycDocs = (apiData) => {
    return apiData.map((item) => {
      const d = item.d
      const t = item.t
      const c = item.c

      let otherDetails = {}
      try {
        otherDetails = d.otherdetails ? JSON.parse(d.otherdetails) : {}
      } catch (e) {
        console.warn("Failed to parse otherdetails:", d.otherdetails)
      }

      const expiryDate = d.expiryDate ? new Date(d.expiryDate) : null
      const today = new Date()
      const status = statusMap[d.status] || "unknown";

      return {
        id: Number.parseInt(d.id),
        categoryId: t.categoryId?.toString() || null,
        subType: d.subType?.toString() || null,
        number: otherDetails?.number || null,
        issueDate: otherDetails?.issueDate ? new Date(otherDetails.issueDate) : null,
        expiryDate: expiryDate,
        dateOfBirth: otherDetails?.dob ? new Date(otherDetails.dob) : null,
        country: otherDetails?.country || null,
        state: otherDetails?.state || null,
        // status: d.status === "2" ? "approved" : d.status === "3" ? "rejected" : "pending",
        status: status,
        isExpired: expiryDate ? expiryDate < today : false,
        uploadDate: d.uploadDate ? new Date(d.uploadDate) : null,
        avatar: d?.docimage || "/api/placeholder/80/80",
        documentImageId: extractDocIdFromAvatar(d?.docimage),
        notes: d?.notes
      }
    })
  }

  const extractDocIdFromAvatar = (avatar) => {
    if (!avatar || avatar === "/api/placeholder/40/40") {
      return null
    }

    const parts = avatar.split("-")
    if (parts.length >= 4) {
      const lastPart = parts[parts.length - 1]
      return lastPart.slice(-4)
    }

    return null
  }

  const getKycCategories = async () => {
    setIsLoading(true)
    try {
      const response = await commonRunQuery(302, "")

      if (response?.data?.header?.callStatus === "1") {
        console.log("Raw API Response:", response?.data?.body?.record)

        const rawRecords = Array.isArray(response?.data?.body?.record)
          ? response.data.body.record
          : [response.data.body.record]

        const categoriesArr = rawRecords.map((item) => {
          const parser = new DOMParser()
          const unescaped = parser.parseFromString(item.record.subtypes, "text/html").body.textContent

          let parsedSubtypes = []
          try {
            parsedSubtypes = JSON.parse(unescaped)
          } catch (err) {
            console.error("JSON parse error for subtypes:", err)
          }

          return {
            id: item.k.id,
            name: item.k.name,
            subTypes: parsedSubtypes,
          }
        })

        console.log("Cleaned Categories:", categoriesArr)
        const lookup = {}
        categoriesArr.forEach((category) => {
          category.subTypes.forEach((subtype) => {
            lookup[subtype.subtypeid] = {
              name: subtype.name,
              categoryId: category.id,
              categoryName: category.name,
            }
          })
        })

        console.log("Subtype Lookup Map:", lookup)

        setSubtypeLookup(lookup)
        setDocumentCategories({
          user: categoriesArr,
          merchant: categoriesArr,
          // merchant: [],
        })

        return categoriesArr
      }
    } catch (error) {
      console.error("getKycCategories error:", error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setExpandedAccordion(null)
    getUserKycData()
  }, [activeTab])

  useEffect(() => {
    getKycCategories()
  }, [])

  const handleTabChange = (event, newValue) => {
    console.log("handleTabChange-------------", newValue);
    setActiveTab(newValue)
  }

  const handleDocumentUpdate = (updatedDoc) => {
    if (activeTab === 0) {
      setUserDocuments((prev) => prev.map((doc) => (doc.id === updatedDoc.id ? updatedDoc : doc)))
    } else {
      setMerchantDocuments((prev) => prev.map((doc) => (doc.id === updatedDoc.id ? updatedDoc : doc)))
    }
  }

  const handleAccordionChange = (categoryId) => (event, isExpanded) => {
    console.log("categoryId--------", categoryId)
    setExpandedAccordion(isExpanded ? categoryId : null)
    callAccordionAPI(categoryId, isExpanded)
  }

  const handleMarkExpired = (docId) => {
    const updateDocs = (docs) =>
      docs.map((doc) => (doc.id === docId ? { ...doc, isExpired: true, status: "expired" } : doc))

    if (activeTab === 0) {
      setUserDocuments(updateDocs)
    } else {
      setMerchantDocuments(updateDocs)
    }
  }

  const handleStatusUpdate = (docId, newStatus, categoryId, note) => {
    const updateDocs = (docs) => docs.map((doc) => (doc.id === docId ? { ...doc, status: newStatus } : doc))

    if (activeTab === 0) {
      setUserDocuments(updateDocs)
    } else {
      setMerchantDocuments(updateDocs)
    }

    refreshDocumentDetails(docId, newStatus, categoryId, note)
  }

  const handleUploadNew = (categoryId, subType, uploadedImageData = null) => {
    console.log(`Upload new document: Category ${categoryId}, Type: ${subType}`)

    if (uploadedImageData) {
      setDocumentImagesMap((prev) => ({
        ...prev,
        [`temp_${categoryId}_${Date.now()}`]: uploadedImageData,
      }))
    }

    getUserKycData(uploadedImageData, categoryId)
  }

  const getStatesForCountry = (countryValue) => {
    if (!countryValue || countryValue === "na") {
      return []
    }

    const countryData = stateData.find((country) => country.iso3 === countryValue)
    if (countryData && countryData.states) {
      return countryData.states.map((state) => ({
        value: state.state_code || state.name,
        label: state.name,
        key: state.state_code || state.name,
      }))
    }
    return []
  }

  const countries = getOptionsForSelect("country")
  const states = stateData
  const currentCategories = activeTab === 0 ? documentCategories.user : documentCategories.merchant
  const currentDocuments = activeTab === 0 ? userDocuments : merchantDocuments
  const currentUserType = activeTab === 0 ? "user" : "merchant"

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {isLoading == true ? (
          <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                overflow: "hidden",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ width: "100%", maxWidth: 1400, mx: "auto", p: 2 }} className="kyc-card-container">
            <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
              <Tab label="User KYC Documents" />
              {(userType === "both" || userType === "merchant") && <Tab label="Merchant KYC Documents" />}
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  User KYC Documents
                </Typography>
                {currentCategories.map((category) => (
                  <DocumentCategorySection
                    key={category.id}
                    category={category}
                    documents={getDocumentsByCategory(category.id, currentDocuments)}
                    onDocumentUpdate={handleDocumentUpdate}
                    onMarkExpired={handleMarkExpired}
                    onUploadNew={handleUploadNew}
                    onStatusUpdate={handleStatusUpdate}
                    countries={countries}
                    states={states}
                    getStatesForCountry={getStatesForCountry}
                    expanded={expandedAccordion === category.id}
                    onAccordionChange={handleAccordionChange(category.id)}
                    userType={currentUserType}
                    subtypeLookup={subtypeLookup}
                    documentImagesMap={documentImagesMap[category.id] || {}}
                    getUserKycData={getUserKycData}
                    activeTab={activeTab}
                  />
                ))}
              </Box>
            )}

            {activeTab === 1 && (userType === "both" || userType === "merchant") && (
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Merchant KYC Documents
                </Typography>
                {console.log("currentCategories------------",currentCategories)}
                {currentCategories.map((category) => (
                  <DocumentCategorySection
                    key={category.id}
                    category={category}
                    documents={getDocumentsByCategory(category.id, currentDocuments)}
                    onDocumentUpdate={handleDocumentUpdate}
                    onMarkExpired={handleMarkExpired}
                    onUploadNew={handleUploadNew}
                    onStatusUpdate={handleStatusUpdate}
                    countries={countries}
                    states={states}
                    getStatesForCountry={getStatesForCountry}
                    expanded={expandedAccordion === category.id}
                    onAccordionChange={handleAccordionChange(category.id)}
                    userType={currentUserType}
                    subtypeLookup={subtypeLookup}
                    documentImagesMap={documentImagesMap[category.id] || {}}
                    getUserKycData={getUserKycData}
                    activeTab={activeTab}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
    </LocalizationProvider>
  )
}

export default KYCUploadComponentFixed
